import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { convertISODateToMMDDYYYY, convertISOToTime, getTimestampDisplay, stringToHexColor } from '@/lib/utils';
import { useUserStore } from '@/stores/user';
import { DeliveryComment } from '@/types/delivery.types';
import { useEffect, useRef, useState } from 'react';
import Spinner from '@/images/spinner.svg';

const Comment = ({ comment }: { comment: DeliveryComment }) => {
  const { timeDisplayText, hasTooltip } = getTimestampDisplay(comment.createdAt);
  const userStore = useUserStore((state) => state.user);
  return (
    <div key={comment.commentId} className={`p-4 border-bordercolor flex flex-col h-fit gap-2`}>
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-2 items-center">
          <span
            className={`rounded-full w-[32px] h-[32px] text-lg focus:outline-none focus-visible:outline-none text-white p-2.5 items-center justify-center flex`}
            style={{ background: stringToHexColor(comment.addedByName) }}
          >
            {comment.addedByName?.charAt(0)}
          </span>
          <span className="text-base font-semibold">{`${comment.addedByName} ${userStore?.userId === comment.addedById ? '(you)' : ''} `}</span>
        </div>
        {hasTooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <span className="text-sm text-gray-500">{timeDisplayText}</span>
              </TooltipTrigger>
              <TooltipContent>
                <span>
                  {convertISODateToMMDDYYYY(comment.createdAt)} {convertISOToTime(comment.createdAt)}
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-placeholder font-normal text-[13px]">{timeDisplayText}</span>
        )}
      </div>
      <div className="text-placeholder font-normal text-sm break-all">{comment.comment}</div>
    </div>
  );
};

const DeliveryCommentsSection = ({
  comments,
  isFetching,
}: {
  isFetching: boolean;
  comments: DeliveryComment[];
}) => {
  const [lastCommentRef, setLastCommentRef] = useState<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (lastCommentRef) lastCommentRef?.scrollIntoView({ behavior: 'auto' });
  }, [lastCommentRef]);

  return (
    <div className="flex flex-col h-full w-full">
      {isFetching && (
        <div className="w-full items-center flex justify-center">
          <img src={Spinner} alt="Loading..." className="h-[25px] w-[25px] animate-spin" />
        </div>
      )}

      {comments.length ? (
        <div className="overflow-auto h-[calc(100vh-245px)] custom-scrollbar" ref={containerRef}>
          {comments.map((comment, index) => {
            const isLast = index === comments.length - 1;
            return (
              <div
                key={comment.commentId}
                className="px-2 whitespace-pre-wrap"
                ref={
                  isLast
                    ? (el) => {
                        setLastCommentRef(el);
                      }
                    : null
                }
              >
                <Comment comment={comment} />
                {!isLast && <hr />}
              </div>
            );
          })}
        </div>
      ) : !isFetching ? (
        <div className="w-full h-full flex justify-center items-center text-placeholder font-base"> No Results</div>
      ) : null}
    </div>
  );
};

export default DeliveryCommentsSection;
